/**
 * Power step: the 3-for-2 bundle step that opens after add to cart on product pages.
 *
 * Document events, all bubbling CustomEvents:
 * - `power-step:opened` — detail `{ productId, variantId, strength, cartItemCount }`.
 *   Fired once the power step is shown for the added line.
 * - `power-step:strength-changed` — detail `{ strength, previous, swapped }`.
 *   Fired when the active strength changes; `swapped` is true when cart lines were changed too.
 * - `power-step:purchase` — detail `{ items, count }`.
 *   Fired after the staged items were added to the cart, before the checkout redirect.
 */
import { debounce, isDesktopViewport } from '../../../scripts/utils/utils.js';
import { savePreferredStrength } from '../../preferredStrength/scripts/preferred-strength.js';
import {
  atScrollTop,
  barVisible,
  cartProducts,
  latchBar,
  decideOpen,
  deriveView,
  findAddedLine,
  GRID_SIZE,
  keepStagedForProducts,
  mergeProducts,
  pairsToSwap,
  planSwap,
  productFromLine,
  sortStrengths,
  stagePair,
  unstagePair,
  variantAtStrength,
  variantsByProduct,
} from './power-step-state.js';

const BODY_OPEN_CLASS = 'power-step-open';
const BODY_DECIDING_CLASS = 'power-step-deciding';
const DECIDING_TIMEOUT = 10000;
const HISTORY_STATE = { powerStep: true };
const GENDER_FILTER = 'Unisex';
const COVERAGE_STEPS = Array.from({ length: 101 }, (_, index) => index / 100);

function readJson(element) {
  try {
    return JSON.parse(element.textContent);
  } catch (error) {
    return null;
  }
}

function productFromCard(card) {
  return {
    productId: Number(card.dataset.productId),
    variantId: Number(card.dataset.variantId),
    title: card.dataset.title,
    image: card.dataset.image,
    model: card.dataset.model,
    available: card.dataset.available === 'true',
  };
}

function cloneTemplate(template) {
  return template.content.firstElementChild.cloneNode(true);
}

function setBusy(region, button, busy) {
  button.disabled = busy;
  region.setAttribute('aria-busy', String(busy));
}

async function postJson(url, body) {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error(`${url} ${response.status}`);
  return response.json();
}

class PowerStep extends HTMLElement {
  constructor() {
    super();
    this.isOpen = false;
    this.pending = false;
    this.lockedScroll = false;
    this.pendingTimer = null;
    this.lastFocus = null;
    this.staged = {};
    this.products = [];
    this.cards = new Map();
    this.strengths = [];
    this.fetchedProducts = [];
    this.fetchedStrength = null;
    this.pendingRemovals = [];
    this.activeStrength = null;
    this.loadToken = 0;
    this.modalBusy = false;
    this.barLatched = false;
    this.barCoverage = 0;
    this.purchasing = false;

    this.onCartAdd = this.onCartAdd.bind(this);
    this.onCartAdded = this.onCartAdded.bind(this);
    this.onPopState = this.onPopState.bind(this);
    this.onKeyDown = this.onKeyDown.bind(this);
    this.onClick = this.onClick.bind(this);
    this.updateSwapQuestion = this.updateSwapQuestion.bind(this);
    this.onModalSubmit = this.onModalSubmit.bind(this);
    this.onModalCancel = this.onModalCancel.bind(this);
    this.onCoverage = this.onCoverage.bind(this);
    this.onScroll = this.onScroll.bind(this);
    this.onResize = debounce(() => {
      if (!this.isOpen) return;
      this.observeCoverage();
      this.updateBarVisibility();
    }, 200);
  }

  connectedCallback() {
    this.product = readJson(this.querySelector('[data-power-step-product]'));
    this.overlay = this.querySelector('[data-power-step-overlay]');
    this.body = this.querySelector('[data-power-step-body]');
    this.strengthChip = this.querySelector('[data-power-step-strength]');
    this.strengthValue = this.querySelector('[data-power-step-strength-value]');
    this.cartCount = this.querySelector('[data-power-step-cart-count]');
    this.offer = this.querySelector('[data-power-step-offer]');
    this.grid = this.querySelector('[data-power-step-grid]');
    this.skeletonTemplate = this.querySelector('[data-power-step-skeleton]');
    this.cardTemplate = this.querySelector('[data-power-step-card-template]');
    this.cartSection = this.dataset.cartSection;
    this.productsSection = this.dataset.productsSection;
    this.productsUrl = this.dataset.productsUrl;
    this.modal = this.querySelector('[data-power-step-modal]');
    this.modalForm = this.querySelector('[data-power-step-modal-form]');
    this.strengthOptions = this.querySelector('[data-power-step-strength-options]');
    this.strengthOptionTemplate = this.querySelector('[data-power-step-strength-option]');
    this.swapBlock = this.querySelector('[data-power-step-swap]');
    this.modalError = this.querySelector('[data-power-step-modal-error]');
    this.confirmButton = this.querySelector('[data-power-step-confirm]');
    this.bar = this.querySelector('[data-power-step-bar]');
    this.barCount = this.querySelector('[data-power-step-count]');
    this.barDone = this.querySelector('[data-power-step-done]');
    this.barError = this.querySelector('[data-power-step-bar-error]');
    this.barSegments = [...this.querySelectorAll('[data-power-step-segment]')];
    this.slots = this.querySelector('[data-power-step-slots]');
    this.slotTemplate = this.querySelector('[data-power-step-slot-template]');
    this.slotEmptyTemplate = this.querySelector('[data-power-step-slot-empty]');
    this.barHead = this.querySelector('[data-power-step-bar-head]');
    this.buyButton = this.querySelector('[data-power-step-buy]');
    this.container = this.querySelector('.power-step__container');
    if (!this.product || !this.overlay) return;

    // The bar grows with its slot titles, so the room reserved under the grid is measured.
    this.barSize = new ResizeObserver(() => this.reserveBarRoom());
    this.barSize.observe(this.bar);

    this.modalForm.addEventListener('change', this.updateSwapQuestion);
    this.modalForm.addEventListener('submit', this.onModalSubmit);
    this.modal.addEventListener('cancel', this.onModalCancel);

    document.addEventListener('theme:cart:add', this.onCartAdd);
    document.addEventListener('theme:cart:added', this.onCartAdded);
    document.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('popstate', this.onPopState);
    window.addEventListener('resize', this.onResize);
    this.addEventListener('click', this.onClick);
  }

  disconnectedCallback() {
    document.removeEventListener('theme:cart:add', this.onCartAdd);
    document.removeEventListener('theme:cart:added', this.onCartAdded);
    document.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('popstate', this.onPopState);
    window.removeEventListener('resize', this.onResize);
    this.removeEventListener('click', this.onClick);
    this.modalForm?.removeEventListener('change', this.updateSwapQuestion);
    this.modalForm?.removeEventListener('submit', this.onModalSubmit);
    this.modal?.removeEventListener('cancel', this.onModalCancel);
    this.observer?.disconnect();
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.barSize?.disconnect();
  }

  /**
   * Only the main product form on this product page can open the power step.
   * Quick view, try-on, and drawer upsells keep the cart drawer.
   * @param {Element|undefined} selector - The clicked add-to-cart element
   * @returns {HTMLFormElement|null}
   */
  productPageForm(selector) {
    const form = selector?.closest?.('form[data-product-form]');
    if (!form || !form.closest('[data-main]')) return null;
    if (form.closest('.popup-quick-view, .to__add-to-cart')) return null;

    const productId = Number(form.querySelector('[name="product-id"]')?.value);
    return productId === this.product.id ? form : null;
  }

  onCartAdd(event) {
    const form = this.productPageForm(event.detail?.selector);
    if (!form) return;

    this.addedLine = {
      variantId: Number(form.querySelector('[name="id"]')?.value),
      productId: this.product.id,
    };
    this.pending = true;
    // The theme moves focus while the drawer opens, so the button is captured now.
    this.lastFocus = event.detail?.selector || form.querySelector('[type="submit"]') || document.activeElement;
    document.body.classList.add(BODY_DECIDING_CLASS);
    clearTimeout(this.pendingTimer);
    this.pendingTimer = setTimeout(() => this.releaseDrawer(), DECIDING_TIMEOUT);
  }

  async onCartAdded() {
    if (!this.pending) return;
    this.pending = false;
    clearTimeout(this.pendingTimer);

    const cart = await this.fetchCart();
    const target = cart
      ? decideOpen({ enabled: true, product: this.product, cartLines: cart.lines })
      : 'drawer';

    if (target === 'power-step') {
      this.open(cart);
    } else {
      this.releaseDrawer();
    }
  }

  async fetchCart() {
    try {
      const response = await fetch(`${window.theme.routes.cart_url}?section_id=${this.cartSection}`);
      if (!response.ok) return null;
      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      return readJson(doc.querySelector('[data-power-step-cart]'));
    } catch (error) {
      return null;
    }
  }

  releaseDrawer() {
    this.pending = false;
    clearTimeout(this.pendingTimer);
    document.body.classList.remove(BODY_DECIDING_CLASS);
  }

  /**
   * Stores the latest cart and refreshes what depends on it. Purchase and swap
   * actions call this after they change the cart.
   * @param {{ itemCount: number, lines: Array }} cart
   */
  setCart(cart) {
    this.cart = cart;
    this.renderHeader();
    this.renderGrid();
  }

  addedCartLine() {
    return this.cart && this.addedLine ? findAddedLine(this.cart.lines, this.addedLine) : null;
  }

  buildProductsUrl(filters) {
    const params = new URLSearchParams({
      section_id: this.productsSection,
      'filter.p.m.custom.glasses_collection': this.product.glassesCollection,
      'filter.p.product_type': this.product.productType,
      'filter.v.availability': '1',
      // The bundle is offered to everyone, so the grid never suggests a
      // men's or women's pair. The gender filter values are Unisex, Women, Men.
      'filter.p.m.custom.glasses_gender': GENDER_FILTER,
    });
    if (this.activeStrength) params.set('filter.v.option.strength', this.activeStrength);
    Object.entries(filters).forEach(([key, value]) => params.set(key, value));
    return `${this.productsUrl}?${params.toString()}`;
  }

  /**
   * One Section Rendering API call. Returns null on any failure so the caller
   * can fall back to the other set.
   * @param {Object<string, string>} filters - Filter params for this set, added to the shared ones
   * @param {AbortSignal} signal
   * @returns {Promise<{ products: Array, cards: Map<number, Element>, strengths: Array }|null>}
   */
  async fetchProducts(filters, signal) {
    try {
      const response = await fetch(this.buildProductsUrl(filters), { signal });
      if (!response.ok) return null;
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const cards = new Map();
      const products = [...doc.querySelectorAll('[data-power-step-card]')].map((card) => {
        const product = productFromCard(card);
        cards.set(product.productId, card);
        return product;
      });
      const strengths = readJson(doc.querySelector('[data-power-step-strengths]')) || [];
      return { products, cards, strengths };
    } catch (error) {
      return null;
    }
  }

  /**
   * Fetches the model set and the shape set in parallel and builds the grid.
   * A newer call cancels an older one, so a strength change never shows stale cards.
   * @param {string|null} [restageAt] - New strength, when the customer swapped the cart
   */
  async loadProducts(restageAt = null) {
    const line = this.addedCartLine();
    if (!line) {
      this.offer.hidden = true;
      this.grid.replaceChildren();
      this.grid.removeAttribute('aria-busy');
      return;
    }

    const token = ++this.loadToken;
    this.controller?.abort();
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.showSkeleton();

    const [model, shape] = await Promise.all([
      this.fetchProducts({ 'filter.p.m.custom.glasses_model': this.product.glassesModel }, signal),
      this.product.shapeGid
        ? this.fetchProducts({ 'filter.p.m.custom.glasses_shapes': this.product.shapeGid }, signal)
        : Promise.resolve(null),
    ]);
    if (token !== this.loadToken) return;

    const failed = !model && !shape;
    this.offer.hidden = failed;
    this.strengths = sortStrengths(model?.strengths || shape?.strengths || []);
    this.fetchedProducts = [...(model?.products || []), ...(shape?.products || [])];
    this.fetchedStrength = this.activeStrength;
    this.products = mergeProducts({
      added: productFromLine(line, this.product.glassesModel),
      cartProducts: cartProducts({ cartLines: this.cart.lines, addedLine: this.addedLine }),
      modelProducts: model?.products || null,
      shapeProducts: shape?.products || null,
      currentModel: this.product.glassesModel,
    });
    this.cards = new Map(this.products.map((product) => [product.productId, this.cardFor(product, model, shape)]));
    this.staged = keepStagedForProducts(this.staged, this.products, restageAt);
    this.grid.removeAttribute('aria-busy');
    this.renderGrid();
  }

  cardFor(product, ...sets) {
    const fetched = sets.find((set) => set?.cards.has(product.productId));
    if (fetched) return fetched.cards.get(product.productId);

    const card = cloneTemplate(this.cardTemplate);
    Object.assign(card.dataset, {
      productId: product.productId,
      variantId: product.variantId,
      title: product.title,
      image: product.image,
      model: product.model || '',
    });
    const img = card.querySelector('[data-power-step-card-img]');
    img.src = product.image;
    img.alt = product.title;
    card.querySelector('[data-power-step-card-title]').textContent = product.title;
    return card;
  }

  showSkeleton() {
    this.products = [];
    this.cards = new Map();
    this.grid.setAttribute('aria-busy', 'true');
    this.grid.replaceChildren(
      ...Array.from({ length: GRID_SIZE }, () => cloneTemplate(this.skeletonTemplate))
    );
  }

  renderGrid() {
    if (!this.cart || !this.addedLine || this.products.length === 0) return;

    this.view = deriveView({
      cartLines: this.cart.lines,
      addedLine: this.addedLine,
      products: this.products,
      staged: this.staged,
    });

    const cards = this.view.cards.map(({ product, quantity, minusEnabled, plusEnabled }) => {
      const card = this.cards.get(product.productId);
      card.querySelector('[data-power-step-quantity]').textContent = quantity;
      card.querySelector('[data-power-step-minus]').disabled = !minusEnabled;
      card.querySelector('[data-power-step-plus]').disabled = !plusEnabled;
      card.querySelector('[data-power-step-card-strength]').textContent = this.activeStrength || '';
      return card;
    });
    this.grid.replaceChildren(...cards);
    this.renderBar();
  }

  renderBar() {
    const { count, finished, mode, slots } = this.view;
    const filled = Math.min(count, this.barSegments.length);
    const justFinished = finished && !this.wasFinished;
    this.wasFinished = finished;
    if (!finished) this.clearScrollRoom();

    this.barSegments.forEach((segment, index) => segment.classList.toggle('is-filled', index < filled));
    this.barCount.textContent = this.bar.dataset.selectedText.replace('[count]', count);
    this.barCount.hidden = finished;
    this.barHead.hidden = finished;
    this.barDone.hidden = !finished;
    this.buyButton.hidden = !finished;
    this.bar.classList.toggle('power-step__bar--inline', mode === 'inline');
    this.bar.classList.toggle('power-step__bar--sticky', mode === 'sticky');
    this.slots.replaceChildren(...slots.map((slot) => this.renderSlot(slot)));
    this.updateBarVisibility();
    if (justFinished) this.scrollToBar();
  }

  /**
   * The bar leaves the bottom of the screen when the set is complete, so the
   * finished view is brought to the middle of the viewport. On desktop the
   * dialog is the scroller, and `scrollIntoView` inside it would move the page
   * behind the dialog instead, so the offset inside the dialog is measured.
   */
  scrollToBar() {
    requestAnimationFrame(() => {
      if (!isDesktopViewport() || !this.overlay) {
        this.bar.scrollIntoView({ behavior: 'smooth', block: 'center' });
        return;
      }
      const bar = this.bar.getBoundingClientRect();
      const view = this.overlay.getBoundingClientRect();
      const top = this.overlay.scrollTop + bar.top - view.top - (view.height - bar.height) / 2;
      // The finished view is the last thing in the dialog, so the room that
      // brings it to the middle has to be added under it first.
      const end = this.overlay.scrollHeight - this.overlay.clientHeight;
      if (top > end) this.overlay.style.paddingBottom = `${Math.ceil(top - end)}px`;
      this.overlay.scrollTo({ top: Math.max(0, top), behavior: 'smooth' });
    });
  }

  clearScrollRoom() {
    if (this.overlay) this.overlay.style.paddingBottom = '';
  }

  renderSlot(slot) {
    if (slot.type === 'empty') return cloneTemplate(this.slotEmptyTemplate);

    const element = cloneTemplate(this.slotTemplate);
    element.classList.add(`power-step__slot--${slot.type}`);
    const img = element.querySelector('[data-power-step-slot-img]');
    const label = element.querySelector('[data-power-step-slot-label]');
    const { title, model } = slot.product;
    const colour = model ? title.replace(model, '').trim() : title;

    img.src = slot.product.image;
    img.alt = title;
    element.querySelector('[data-power-step-slot-model]').textContent = model || title;
    element.querySelector('[data-power-step-slot-color]').textContent = colour;
    element.querySelector('[data-power-step-slot-strength]').textContent = slot.strength || '';
    if (slot.type === 'cart') {
      label.textContent = this.bar.dataset.inCartText;
    } else {
      label.remove();
    }
    return element;
  }

  /**
   * The bar follows the bundle section into view. On desktop the overlay is
   * the scroll container, on mobile the document is, so the root differs.
   * Thresholds are 1% steps of the section, so a tall grid still reports
   * viewport coverage without visible lag.
   */
  observeCoverage() {
    this.observer?.disconnect();
    const root = isDesktopViewport() ? this.overlay : null;
    this.observer = new IntersectionObserver(this.onCoverage, { root, threshold: COVERAGE_STEPS });
    this.observer.observe(this.body);

    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.scroller = root || window;
    this.scroller.addEventListener('scroll', this.onScroll, { passive: true });
  }

  scrollTop() {
    return isDesktopViewport() ? this.overlay.scrollTop : window.scrollY;
  }

  // The observer only reports threshold crossings, so the return to the top,
  // which releases the latch, is read from the scroller itself.
  onScroll() {
    this.updateLatch();
  }

  updateLatch() {
    this.barLatched = latchBar({
      latched: this.barLatched,
      coverage: this.barCoverage,
      atTop: atScrollTop(this.scrollTop()),
    });
    this.updateBarVisibility();
  }

  /**
   * Coverage is measured against the shorter of viewport and section, so a
   * grid shorter than 80% of a tall screen can still fill its share.
   */
  onCoverage(entries) {
    const entry = entries[entries.length - 1];
    const rootHeight = entry.rootBounds?.height || window.innerHeight;
    const reference = Math.min(rootHeight, entry.boundingClientRect.height);
    this.barCoverage = reference > 0 ? entry.intersectionRect.height / reference : 0;
    this.updateLatch();
  }

  updateBarVisibility() {
    if (!this.view) return;
    const shown = barVisible({
      finished: this.view.finished,
      latched: this.barLatched,
      desktop: isDesktopViewport(),
    });
    this.bar.hidden = false;
    this.bar.classList.toggle('power-step__bar--stowed', !shown);
    this.reserveBarRoom(shown);
  }

  reserveBarRoom(shown = !this.bar.classList.contains('power-step__bar--stowed')) {
    const sticky = this.view && !this.view.finished && shown;
    const height = sticky ? this.bar.getBoundingClientRect().height : 0;
    this.container.style.setProperty('--power-step-bar-height', `${Math.ceil(height)}px`);
  }

  setPurchasing(busy) {
    this.purchasing = busy;
    setBusy(this.bar, this.buyButton, busy);
    this.buyButton.classList.toggle('is-loading', busy);
  }

  /**
   * Adds the staged pairs and goes to checkout. Nothing staged means the cart
   * already holds the set, so checkout opens at once.
   */
  async purchase() {
    if (this.purchasing || !this.view?.finished) return;

    this.setPurchasing(true);
    this.barError.hidden = true;
    const items = this.view.payload;

    try {
      if (items.length > 0) {
        await postJson(`${window.theme.routes.root}cart/add.js`, { items });
      }
      document.dispatchEvent(
        new CustomEvent('power-step:purchase', { bubbles: true, detail: { items, count: this.view.count } })
      );
      window.location.href = `${window.theme.routes.root}checkout`;
    } catch (error) {
      this.setPurchasing(false);
      this.barError.hidden = false;
      await this.reloadCart().catch(() => {});
    }
  }

  stage(productId, delta) {
    const card = this.view?.cards.find((entry) => entry.product.productId === productId);
    if (!card) return;
    if (delta < 0 ? !card.minusEnabled : !card.plusEnabled) return;

    this.staged =
      delta > 0 ? stagePair(this.staged, card.product, this.activeStrength) : unstagePair(this.staged, productId);
    this.renderGrid();
  }

  openStrengthModal() {
    if (!this.modal || this.strengths.length === 0) return;

    const options = this.strengths.map(({ value, label }) => {
      const option = cloneTemplate(this.strengthOptionTemplate);
      const input = option.querySelector('input');
      input.value = value;
      input.checked = value === this.activeStrength;
      option.querySelector('[data-power-step-strength-label]').textContent = label || value;
      return option;
    });
    this.strengthOptions.replaceChildren(this.strengthOptions.querySelector('legend'), ...options);
    this.swapBlock.querySelector('input[value="yes"]').checked = true;
    this.modalError.hidden = true;
    this.updateSwapQuestion();
    this.modal.showModal();
  }

  closeStrengthModal() {
    if (this.modalBusy) return;
    this.modal.close();
  }

  selectedStrength() {
    return this.modalForm.querySelector('input[name="power-step-strength"]:checked')?.value || null;
  }

  swapInput(targetStrength) {
    return { cartLines: this.cart.lines, addedLine: this.addedLine, targetStrength };
  }

  updateSwapQuestion() {
    const targetStrength = this.selectedStrength();
    const pairs = targetStrength ? pairsToSwap(this.swapInput(targetStrength)) : [];
    this.swapBlock.hidden = pairs.length === 0;
  }

  setModalBusy(busy) {
    this.modalBusy = busy;
    setBusy(this.modal, this.confirmButton, busy);
  }

  onModalCancel(event) {
    if (this.modalBusy) event.preventDefault();
  }

  /**
   * Applies the chosen strength to the grid, then swaps cart lines when asked.
   * The grid change stands even when the swap fails; the modal then stays open
   * with the error so the customer can retry the swap alone.
   */
  async onModalSubmit(event) {
    event.preventDefault();
    if (this.modalBusy) return;

    const strength = this.selectedStrength();
    if (!strength) return;
    const swap = !this.swapBlock.hidden && this.modalForm.querySelector('input[name="power-step-swap"]:checked')?.value === 'yes';
    const changed = strength !== this.activeStrength;
    if (!changed && !swap) {
      this.modal.close();
      return;
    }

    this.setModalBusy(true);
    this.modalError.hidden = true;
    const previous = this.activeStrength;
    this.activeStrength = strength;
    if (changed) {
      // A strength picked here is as deliberate as one picked on the product page.
      savePreferredStrength(strength);
      await this.loadProducts(swap ? strength : null);
    }

    const result = swap ? await this.swapCart(strength) : 'nothing';
    this.setModalBusy(false);
    if (changed) {
      document.dispatchEvent(
        new CustomEvent('power-step:strength-changed', {
          bubbles: true,
          detail: { strength, previous, swapped: result === 'swapped' },
        })
      );
    }

    if (result === 'failed') {
      // Escape can close a busy dialog despite the cancel guard, so the error must reopen it.
      if (!this.modal.open) this.modal.showModal();
      this.modalError.hidden = false;
      this.updateSwapQuestion();
      return;
    }
    this.modal.close();
  }

  /**
   * Moves the cart lines in the collection to the target strength. Adds run
   * before removals so a failure half way never loses the customer's pairs;
   * removals left over from such a failure are finished first on the next call.
   * @param {string} strength
   * @returns {Promise<'swapped'|'nothing'|'failed'>}
   */
  async swapCart(strength) {
    try {
      await this.finishPendingRemovals();

      const lines = pairsToSwap(this.swapInput(strength));
      const variantByProductId = await this.variantsAtStrength(lines, strength);
      const plan = planSwap({ ...this.swapInput(strength), variantByProductId, swap: true });
      if (!plan) return 'nothing';

      const addedKey = this.addedCartLine()?.key;
      await postJson(`${window.theme.routes.root}cart/add.js`, { items: plan.adds });
      this.pendingRemovals = plan.changes.map(({ id }) => id);
      await this.removeLines(this.pendingRemovals);
      this.pendingRemovals = [];

      if (plan.changes.some(({ id }) => id === addedKey)) {
        this.addedLine = { ...this.addedLine, variantId: variantByProductId[this.addedLine.productId] };
      }
      await this.reloadCart();
      return 'swapped';
    } catch (error) {
      await this.reloadCart().catch(() => {});
      return 'failed';
    }
  }

  async finishPendingRemovals() {
    const keys = this.pendingRemovals.filter((key) => this.cart.lines.some((line) => line.key === key));
    if (keys.length === 0) return;
    await this.removeLines(keys);
    this.pendingRemovals = [];
    await this.reloadCart();
  }

  removeLines(keys) {
    return postJson(`${window.theme.routes.root}cart/update.js`, {
      updates: Object.fromEntries(keys.map((key) => [key, 0])),
    });
  }

  async reloadCart() {
    const cart = await this.fetchCart();
    if (!cart) throw new Error('cart reload failed');
    this.setCart(cart);
  }

  /**
   * Variant id at the strength per product id. The grid sets fetched at that
   * strength already carry it; other cart lines use the product JSON endpoint.
   * @param {Array} lines - Cart lines that need a variant
   * @param {string} strength
   * @returns {Promise<Object<number, number>>}
   */
  async variantsAtStrength(lines, strength) {
    const map = this.fetchedStrength === strength ? variantsByProduct(this.fetchedProducts) : {};
    const missing = new Map(lines.filter((line) => !map[line.productId] && line.handle).map((line) => [line.productId, line.handle]));

    await Promise.all(
      [...missing].map(async ([productId, handle]) => {
        const variantId = await this.fetchVariantAt(handle, strength);
        if (variantId) map[productId] = variantId;
      })
    );
    return map;
  }

  async fetchVariantAt(handle, strength) {
    try {
      const response = await fetch(`${window.theme.routes.root}products/${handle}.js`);
      if (!response.ok) return null;
      return variantAtStrength(await response.json(), this.product.strengthOptionName, strength);
    } catch (error) {
      return null;
    }
  }

  renderHeader() {
    const line = this.addedCartLine();
    if (!line) return;
    const strength = line.strength;

    this.cartCount.textContent = this.cart.itemCount;
    this.strengthChip.hidden = !strength;
    this.strengthValue.textContent = strength || '';
  }

  open(cart) {
    if (this.isOpen) return;
    this.staged = {};
    this.products = [];
    this.view = null;
    this.barLatched = false;
    this.barCoverage = 0;
    this.wasFinished = false;
    this.clearScrollRoom();
    this.bar.hidden = true;
    this.activeStrength = findAddedLine(cart.lines, this.addedLine)?.strength || null;
    this.setCart(cart);
    this.isOpen = true;
    this.barError.hidden = true;
    this.setPurchasing(false);
    this.loadProducts();
    this.observeCoverage();

    // The theme closes its cart drawer on this event. The drawer was already open but hidden while deciding.
    document.dispatchEvent(new CustomEvent('theme:popup:open', { bubbles: true }));
    document.body.classList.remove(BODY_DECIDING_CLASS);
    document.body.classList.add(BODY_OPEN_CLASS);
    window.history.pushState(HISTORY_STATE, '');

    if (isDesktopViewport()) {
      this.lockedScroll = true;
      this.overlay.scrollTop = 0;
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true, detail: this.overlay }));
    } else {
      window.scrollTo({ top: 0 });
    }

    // The drawer close blurs the active element on the next frame, so focus lands after it.
    requestAnimationFrame(() => requestAnimationFrame(() => this.overlay.focus({ preventScroll: true })));

    document.dispatchEvent(
      new CustomEvent('power-step:opened', {
        bubbles: true,
        detail: {
          productId: this.addedLine.productId,
          variantId: this.addedLine.variantId,
          strength: this.activeStrength,
          cartItemCount: cart.itemCount,
        },
      })
    );
  }

  /**
   * Closes through history when the power step owns the current entry,
   * so the back button and the close controls leave the same history.
   */
  requestClose() {
    if (!this.isOpen) return;
    if (window.history.state?.powerStep) {
      window.history.back();
    } else {
      this.close();
    }
  }

  close() {
    if (!this.isOpen) return;
    this.isOpen = false;
    this.observer?.disconnect();
    this.scroller?.removeEventListener('scroll', this.onScroll);
    this.scroller = null;
    document.body.classList.remove(BODY_OPEN_CLASS);

    if (this.lockedScroll) {
      this.lockedScroll = false;
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }

    if (this.lastFocus?.focus) this.lastFocus.focus({ preventScroll: true });
    this.lastFocus = null;
  }

  onPopState() {
    if (this.isOpen) this.close();
  }

  onKeyDown(event) {
    if (event.key !== 'Escape' || !this.isOpen) return;
    // An open strength modal owns Escape through its own cancel event.
    if (!this.modal?.open) this.requestClose();
  }

  /**
   * Hands the customer over to the theme's cart drawer. The drawer opens from
   * its own toggle, which is the only way into the theme's open routine, and
   * the step closes first so the page and the history entry come back.
   * @returns {boolean} False when the theme has no drawer, so the link navigates
   */
  openCartDrawer() {
    const toggle = document.querySelector('[data-cart-drawer-toggle]');
    if (!toggle) return false;

    this.close();
    if (window.history.state?.powerStep) window.history.back();
    toggle.click();
    return true;
  }

  onClick(event) {
    if (event.target.closest('[data-power-step-cart-link]')) {
      if (this.openCartDrawer()) event.preventDefault();
      return;
    }
    // The desktop overlay covers the backdrop element, so a click on the room
    // around the dialog panel lands here instead.
    if (event.target === this.overlay && isDesktopViewport()) {
      this.requestClose();
      return;
    }
    if (event.target.closest('[data-power-step-close]')) {
      this.requestClose();
      return;
    }
    if (event.target.closest('[data-power-step-change]')) {
      this.openStrengthModal();
      return;
    }
    if (event.target.closest('[data-power-step-buy]')) {
      this.purchase();
      return;
    }
    // A click on the dialog itself is a click on its backdrop.
    if (event.target === this.modal || event.target.closest('[data-power-step-modal-close]')) {
      this.closeStrengthModal();
      return;
    }

    const stepper = event.target.closest('[data-power-step-plus], [data-power-step-minus]');
    if (!stepper) return;
    const productId = Number(stepper.closest('[data-power-step-card]').dataset.productId);
    this.stage(productId, stepper.hasAttribute('data-power-step-plus') ? 1 : -1);
  }
}

if (!customElements.get('power-step')) {
  customElements.define('power-step', PowerStep);
}
